import * as React from 'react';

import { PolicyResponse } from '@ariksa/abac/api';
import { Box, Center, Stack, Text, useDisclosure } from '@chakra-ui/react';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-use';
import { customTheme } from 'theme';

import {
  EllipsisText,
  formatTooltip,
  renderTime,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { ActionButton, DeleteActionButton } from 'components/DataEntry';
import { EditIcon, IconTypes } from 'components/Icons';
import { DataPolicyDrawer } from 'containers/Jit/MaskingPolicies/Components/Drawer/DataPolicyDrawer';
import { resolveDefaultMaskOption } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/maskOptions';
import { seletMaskingPolicies } from 'containers/Jit/MaskingPolicies/selectors';
import { actions } from 'containers/Jit/MaskingPolicies/slice';

export const DataPoliciesTable = () => {
  const dispatch = useDispatch();
  const { dataPolicies, policyAction } = useSelector(seletMaskingPolicies);
  const navigate = useNavigate();

  const disclosure = useDisclosure();
  const [currentRecord, setCurrentRecord] = React.useState<PolicyResponse>(
    {} as PolicyResponse,
  );

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Name</Box>,
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={IconTypes.PolicyScroll}
          iconStyles={{ p: 1 }}
          color={customTheme.colors.blue['300']}
        >
          {row?.name}
        </WithResourceIcon>
      ),
    },
    {
      header: 'Description',
      accessor: 'description',
      align: 'left',
      render: ({ row }) => (
        <Text whiteSpace="wrap" noOfLines={2}>
          {row?.description}
        </Text>
      ),
      styles: { maxW: '35%', cell: { w: '35%' } },
    },
    {
      header: 'Tag',
      accessor: 'policy_json.tag_name',
      align: 'left',
      render: ({ row }) => row?.policy_json?.tag_name?.split('.').pop() ?? '-',
    },

    {
      header: 'Roles',
      accessor: 'roles',
      align: 'left',
      render: ({ row }) => {
        const roleNames = map(row?.roles, role => role.role_name).filter(
          Boolean,
        );
        const visibleRoleNames = roleNames.slice(0, 2);
        const remainingRoleCount = roleNames.length - visibleRoleNames.length;

        return !!roleNames.length ? (
          <EllipsisText
            label={`${visibleRoleNames.join(', ')}${
              remainingRoleCount ? ` +${remainingRoleCount}` : ''
            }`}
            tooltip={formatTooltip({
              header: 'Roles',
              content: (
                <Stack>
                  {map(roleNames, (role, index) => (
                    <Box key={`${role}-${index}`}>
                      {index + 1}. {role}
                    </Box>
                  ))}
                </Stack>
              ),
              width: 64,
            })}
          />
        ) : (
          '-'
        );
      },
    },
    {
      header: 'Default Masking',
      accessor: 'policy_json.default_mask_config.method',
      align: 'left',
      // A policy stored without a default_mask_config compiles to a full mask
      // in the CASE ELSE, so an absent method reads as Full Mask.
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={IconTypes.Mask}
          color={customTheme.colors.blue['300']}
        >
          {
            resolveDefaultMaskOption(
              row?.policy_json?.default_mask_config?.method,
            ).label
          }
        </WithResourceIcon>
      ),
    },
    {
      header: 'Created',
      accessor: 'created_at',
      align: 'left',
      render: renderTime,
    },
    {
      header: 'Actions',
      render: ({ row }) => {
        return (
          <Center onClick={e => e.stopPropagation()}>
            <ActionButton
              label="Edit data Policy"
              onClick={() => navigate(`edit/${row?.policy_id}`)}
              icon={<EditIcon />}
              tooltipWidth={20}
            />
            <DeleteActionButton
              name={row.name}
              onConfirm={() =>
                dispatch(
                  actions.deletePolicy({
                    q: { policyId: row?.policy_id, deleteDeployments: true },
                    onSuccess: () =>
                      dispatch(
                        actions.getDataPolicies({
                          q: {
                            page: dataPolicies.page.info.page_number,
                            size: dataPolicies.page.info.page_size,
                          },
                        }),
                      ),
                  }),
                )
              }
              isLoading={policyAction.isLoading}
              type="data-policy"
              label="Delete data policy"
              tooltipStyles={{ tooltip: { w: 24, textAlign: 'center' } }}
            />
          </Center>
        );
      },
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        data={dataPolicies.data}
        isLoading={dataPolicies.isLoading}
        onRowClick={row => {
          disclosure.onOpen();
          setCurrentRecord(row);
        }}
      />
      {disclosure.isOpen && (
        <DataPolicyDrawer {...disclosure} row={currentRecord} />
      )}
    </>
  );
};
